import { css, type Theme } from "@emotion/react"
import { graphql } from "@myvp/shared/src/gql"

export const KEYS = {
  tab: "Tab",
  arrowDown: "ArrowDown",
  arrowLeft: "ArrowLeft",
  arrowRight: "ArrowRight",
  arrowUp: "ArrowUp",
  backspace: "Backspace",
  enter: "Enter",
  escape: "Escape",
  space: " ",
}
// https://testing-library.com/docs/ecosystem-user-event/#special-characters
export const TEST_KEYS = {
  arrowDown: "{arrowdown}",
  arrowLeft: "{arrowleft}",
  arrowRight: "{arrowright}",
  arrowUp: "{arrowup}",
  backspace: "{backspace}",
  enter: "{Enter}",
  escape: "{Escape}",
  space: "[Space]",
  delete: "{delete}",
}

export const headerHeight = 52

export const headerCss = (theme: Theme) => css`
  background-color: ${theme.palette.surface.vault.default};
  height: ${headerHeight}px;
  flex-shrink: 0;
  padding-inline: 24px 20px;
`

export const VAULT_CLINICAL_URL = "https://clinical.veevavault.help/en/gr"

/**
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
 */
export const IMAGE_TYPE_MAPPING = {
  "image/gif": "GIF",
  "image/jpeg": "JPEG",
  "image/png": "PNG",
}
export const VIDEO_TYPE_MAPPING = {
  "video/mp4": "MP4",
}

/**
 * Example domains may be https://patients-us-dev-ft.myveevatrials.com, https://patients.myveeva.com, https://sponsors-us.local.com:3005
 *
 * These would return myveevatrials.com, myveeva.com, local.com:3005 respectively
 */
export const GLOBAL_DOMAIN = window.location.hostname.substring(
  window.location.hostname.indexOf(".") + 1
)

/**
 * These are from the backend https://gitlab.veevadev.com/myveeva-clinicaltrials/jenkins-libraries/-/blob/develop/vars/translationQualityControl.groovy?ref_type=heads#L9
 */
export const DO_NOT_TRANSLATE_CONSTANTS = {
  myveevaforpatients: "MyVeeva for Patients",
  veevasystems: "Veeva Systems",
  veevasystemsinc: "Veeva Systems Inc.",
  myveevastudio: "MyVeeva Studio",
  econsent: "eConsent",
  veevaEconsent: "Veeva eConsent",
  icf: "ICF",
  myveeva: "MyVeeva",
  sitevault: "SiteVault",
  vault: "Vault",
  uat: "UAT",
  veeva: "Veeva",
  veevaecoa: "Veeva eCOA",
  veevaedc: "Veeva EDC",
  epro: "ePRO",
  ecoa: "eCOA",
  eclinro: "eClinRO",
  clinro: "ClinRO",
  awscloudtrail: "AWS CloudTrail",
  awsguardduty: "AWS Guard Duty",
  awsshield: "AWS Shield",
  aws: "AWS",
  cloudtrail: "CloudTrail",
  amazonwebservices: "Amazon Web Services",
  jpgExtension: ".jpg",
  pngExtension: ".png",
  gifExtension: ".gif",
  pdfExtension: ".pdf",
  zipExtension: ".zip",
  pdfExtensionUppercase: ".PDF",
  jpgExtensionUppercase: ".JPG",
  pngExtensionUppercase: ".PNG",
  gifExtensionUppercase: ".GIF",
  docxExtensionUppercase: ".DOCX",
  zipExtensionUppercase: ".ZIP",
  pdfLabel: "PDF",
} as const

export const TIMEPOINT_LABEL_TOKEN = "timepointLabel"
export const TIMEPOINT_LABEL = `{{${TIMEPOINT_LABEL_TOKEN}}}`

export const DAY_OF_WEEK_TOKEN = "dayOfWeek"
export const DAY_OF_WEEK = `{{${DAY_OF_WEEK_TOKEN}}}`

export const SCORE_TOKEN = "score"
export const SCORE = `{{${SCORE_TOKEN}}}`

export const ACTIVATION_CODE_LENGTH = 6
export const CODE_INPUT_ANIMATION_TIME = 1800

export const GLOBAL_TERMS_URL = "https://patients.myveeva.com/terms"
export const GLOBAL_PRIVACY_URL = "https://patients.myveeva.com/privacy"

export const QuestionBlockResponseFragment = graphql(/* GraphQL */ `
  fragment QuestionBlockResponse on QuestionBlockResponse {
    optionalAnswer {
      type
      name
      blockAnswerUpdatedAt
    }
    answer {
      __typename
      type
      blockAnswerUpdatedAt
      ... on NameArrayAnswer {
        nameArray
      }
      ... on NameValueBlockAnswer {
        name
      }
      ... on NumberValueBlockAnswer {
        number
      }
      ... on MeasurementAnswer {
        components {
          name
          value
        }
      }
      ... on DateTimeAnswer {
        ...DateTimeAnswer
      }
      ... on StringValueBlockAnswer {
        string
      }
      ... on InteractiveImageAnswer {
        interactiveImageNameArray: nameArray
        interactiveImageSelections: selections {
          selectedNames
          unselectedNames
        }
        interactiveImageAnswerType
        url
      }
      ... on LinkBlockAnswer {
        ...LinkBlockAnswer
      }
      ... on MediaUploadBlockAnswer {
        mediaUploadUrl: url
        mediaUploadType
      }
    }
  }
`)

export const BlocksQueryFragment = graphql(/* GraphQL */ `
  fragment BlocksQuery on Block {
    __typename
    name
    condition
    tablePosition
    validationSettings {
      validation
      message
    }
    headingAttachment {
      id
      attachmentType
      url
      description
      imageProperties {
        width
        height
      }
      maxHeightPercentage
    }
    ... on Text {
      heading
    }
    ... on Question {
      heading
      name
      questionNumber
      optionalAnswerSet {
        optionalAnswers {
          answer
          name
        }
      }
    }
    ... on SingleChoiceQuestion {
      answerSet {
        answers {
          answer
          name
          answerAttachment {
            url
            description
            imageProperties {
              height
              width
            }
          }
        }
      }
      blockSettings {
        answerHeight
        displayAsDropdown
      }
    }
    ... on MultipleChoiceQuestion {
      answerSet {
        answers {
          answer
          name
          answerAttachment {
            url
            description
            imageProperties {
              height
              width
            }
          }
        }
      }
      blockSettings {
        answerHeight
        displayAsDropdown
      }
    }
    ... on VisualScaleQuestion {
      blockSettings {
        minNumber
        maxNumber
        orientation
        visualScaleDisplayType: displayType
        visualScaleIncrement: increment
        minLabel
        maxLabel
        markDisplayInterval
        markNumberInterval
        displayResult
        customMarks {
          label
          positions
        }
        visualScalePlacement: placement
        answerAttachment {
          url
          description
          imageProperties {
            width
            height
          }
        }
        showPercent
      }
    }
    ... on NumberEntryQuestion {
      answerSet {
        categories {
          answers {
            name
            label
            placeholder: placeholderValue
            minNumber
            maxNumber
            increment
          }
        }
      }
    }
    ... on NumberScaleQuestion {
      blockSettings {
        answerAttachment {
          url
          description
          imageProperties {
            width
            height
          }
        }
        minNumber
        maxNumber
        numberScaleIncrement: increment
        showPercent
        placement
        direction
        customMarks {
          positions
          label
        }
      }
    }
    ... on DateTimeQuestion {
      dateTimeFormat
      blockSettings {
        minimumValue {
          ...DateTimeValue
        }
        maximumValue {
          ...DateTimeValue
        }
        defaultedValue {
          ...DateTimeValue
        }
      }
    }
    ... on TextEntryQuestion {
      blockSettings {
        label
        maxLength
        placeholder
      }
    }
    ... on InteractiveImageQuestion {
      heading
      interactiveImageType
      blockSettings {
        ... on JointCountInteractiveImageBlockSettings {
          countLabel
          displayCount
          __typename
        }
        ... on PainDetectInteractiveImageBlockSettings {
          primaryBlockName
          secondaryBlockName
          __typename
        }
        ... on FreeDrawInteractiveImageBlockSettings {
          __typename
        }
      }
    }
    ... on LinkQuestion {
      blockSettings {
        relatedSurveyId
        summaryBlockNames
        label
        placeholder
        emptyState
        listRange
      }
    }
  }
`)

export const dateTimeFragment = graphql(/* GraphQL */ `
  fragment DateTimeAnswer on DateTimeAnswer {
    __typename
    type
    blockAnswerUpdatedAt
    dateTimeValue {
      ...DateTimeValue
    }
  }
`)

export const DateTimeValueFragment = graphql(/* GraphQL */ `
  fragment DateTimeValue on DateTimeValueInterface {
    __typename
    dateTimeValueType
    ... on StaticDateTimeValue {
      dateTimeFormat
      value
    }
    ... on DynamicDateTimeValue {
      offset {
        value
        unit
      }
    }
  }
`)

export const BlockResponse = graphql(/* GraphQL */ `
  fragment BlockResponse on BlockResponse {
    __typename
    name
    blockStatus
    blockUpdatedAt
    ... on QuestionBlockResponse {
      ...QuestionBlockResponse
    }
  }
`)

export const QuestionAndResponseFragment = graphql(/* GraphQL */ `
  fragment QuestionAndResponse on QuestionAndResponse {
    question {
      ... on Block {
        ...BlocksQuery
      }
    }
    response {
      __typename
      name
      blockStatus
      blockUpdatedAt
      optionalAnswer {
        type
        name
        blockAnswerUpdatedAt
      }
      answer {
        __typename
        blockAnswerUpdatedAt
        type
        ... on NameArrayAnswer {
          nameArray
        }
        ... on NameValueBlockAnswer {
          name
        }
        ... on NumberValueBlockAnswer {
          number
        }
        ... on MeasurementAnswer {
          components {
            name
            value
          }
        }
        ... on DateTimeAnswer {
          ...DateTimeAnswer
        }
        ... on StringValueBlockAnswer {
          string
        }
      }
    }
  }
`)

// LinkBlockAnswer needs to be defined separately because just spreading ...BlockResponse on
// questionAndResponseList.response causes a infinite loop. (i.e. we can't have LinkBlockAnswer have
// a response LinkBlockAnswer which can have a response LinkBlockAnswer etc.

// A link block cannot be linked to another link block, a interactiveImage block, or text block. The
// response in questionAndResponseList should never return the potentially infinite loop described
// above because link blocks are prevented from linking to another link block. The answer also doesn't
// include InteractiveImageAnswer below because it is prevented in the survey JSON as well.
export const LinkBlockAnswerFragment = graphql(/* GraphQL */ `
  fragment LinkBlockAnswer on LinkBlockAnswer {
    userSurveyId
    blockAnswerUpdatedAt
    surveyCompletedAt
    surveyDisplayName
    blockAnswerUpdatedAt
    questionAndResponseList {
      ...QuestionAndResponse
    }
  }
`)
